import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useArticles } from "@/hooks/useArticles";
import SectionHeader from "@/components/base/SectionHeader";

function formatDate(dateStr: string, lang: string) {
  return new Date(dateStr).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { month: "short", year: "numeric" });
}

export default function JeunesTalentsSection() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("fr") ? "fr" : "en";
  const { articles, loading } = useArticles({ category: "Jeune Talent", limit: 5 });
  const [hero, ...rest] = articles;

  return (
    <section className="py-16 bg-[#fdf8f3]">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <SectionHeader
          title={t("section.jeunesTalents.title")}
          subtitle={t("section.jeunesTalents.subtitle")}
          viewAllLink="/jeunes-talents"
          accent="purple"
        />

        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 bg-gray-100 rounded-2xl h-80 animate-pulse"></div>
            <div className="lg:col-span-2 grid grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-2xl h-36 animate-pulse"></div>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {hero && (
              <Link
                to={`/article/${hero.id}`}
                className="group relative rounded-2xl overflow-hidden cursor-pointer block lg:col-span-1"
                style={{ minHeight: "380px" }}
              >
                <img src={hero.image} alt={hero.title} className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent"></div>
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span className="bg-violet-500 text-white text-xs font-bold px-3 py-1 rounded-full">{t("section.jeunesTalents.title")}</span>
                  <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-medium px-2 py-1 rounded-full">{formatDate(hero.date, lang)}</span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="text-violet-300 text-xs mb-2 flex items-center gap-1">
                    <span>{hero.countryFlag}</span>
                    <span>{hero.country}</span>
                    <span className="opacity-50">·</span>
                    <span>{hero.sector}</span>
                  </div>
                  <h3 className="text-white font-bold text-base leading-snug line-clamp-3 mb-3 group-hover:text-violet-300 transition-colors">{hero.title}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-white/60 text-xs flex items-center gap-1">
                      <i className="ri-user-line"></i>{hero.entrepreneur}
                    </span>
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-violet-500 text-white group-hover:bg-violet-400 transition-colors">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {rest.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.id}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-violet-200 hover:-translate-y-1 transition-all duration-300 cursor-pointer block"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute top-3 left-3">
                      <span className="bg-violet-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">{article.sector}</span>
                    </div>
                    <div className="absolute bottom-3 right-3 bg-black/40 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-full">
                      {formatDate(article.date, lang)}
                    </div>
                  </div>
                  <div className="p-4">
                    <h4 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2 group-hover:text-violet-600 transition-colors mb-2">{article.title}</h4>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <span>{article.countryFlag}</span>
                        <span>{article.country}</span>
                      </span>
                      <span className="text-violet-500 text-xs font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                        {t("section.read")} <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
